/**
 * Multiselect Dropdown Component
 *
 * A reusable multi-select checkbox dropdown with URL-based filtering.
 * Automatically initializes any element with [data-multiselect="true"].
 *
 * Data attributes:
 *   data-multiselect="true"     - Enables the component
 *   data-name="filter"          - URL parameter name
 *   data-base-url="/path/"      - Base URL for navigation
 *   data-preserve-params="{}"   - JSON object of params to preserve
 */

class MultiselectDropdown {
	constructor(element) {
		this.dropdown = element;
		this.toggle = element.querySelector('.multiselect-dropdown__toggle');
		this.list = element.querySelector('.multiselect-dropdown__list');
		this.checkboxes = element.querySelectorAll('.multiselect-dropdown__checkbox');

		// Get configuration from data attributes
		this.name = element.dataset.name || 'filter';
		this.baseUrl = element.dataset.baseUrl || window.location.pathname;
		this.preserveParams = this.parsePreserveParams(element.dataset.preserveParams);
		this.useAjax = element.dataset.ajax === 'true';
		this.closeOnOutsideClick = element.dataset.closeOnOutsideClick !== 'false';

		this.init();
	}

	parsePreserveParams(jsonString) {
		if (!jsonString) return {};
		try {
			return JSON.parse(jsonString);
		} catch (e) {
			console.warn('MultiselectDropdown: Invalid preserve-params JSON', e);
			return {};
		}
	}

	init() {
		// Toggle dropdown
		this.toggle.addEventListener('click', (e) => {
			e.preventDefault();
			this.toggleOpen();
		});

		// Handle checkbox changes
		this.checkboxes.forEach((checkbox) => {
			checkbox.addEventListener('change', () => {
				this.handleCheckboxChange(checkbox);
			});
		});

		// Close on outside click (if enabled)
		if (this.closeOnOutsideClick) {
			document.addEventListener('click', (e) => {
				if (!this.dropdown.contains(e.target)) {
					this.close();
				}
			});

			// Close on Escape
			document.addEventListener('keydown', (e) => {
				if (e.key === 'Escape') {
					this.close();
				}
			});
		}

		// Keyboard navigation
		this.toggle.addEventListener('keydown', (e) => {
			if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				this.open();
				const firstCheckbox = this.list.querySelector('.multiselect-dropdown__checkbox');
				if (firstCheckbox) {
					firstCheckbox.focus();
				}
			}
		});
	}

	toggleOpen() {
		if (this.isOpen()) {
			this.close();
		} else {
			this.open();
		}
	}

	isOpen() {
		return this.dropdown.classList.contains('multiselect-dropdown--open');
	}

	open() {
		this.dropdown.classList.add('multiselect-dropdown--open');
		this.toggle.setAttribute('aria-expanded', 'true');
	}

	close() {
		this.dropdown.classList.remove('multiselect-dropdown--open');
		this.toggle.setAttribute('aria-expanded', 'false');
	}

	handleCheckboxChange(checkbox) {
		const option = checkbox.closest('.multiselect-dropdown__option');

		// Update selected state styling
		if (checkbox.checked) {
			option.classList.add('multiselect-dropdown__option--selected');
		} else {
			option.classList.remove('multiselect-dropdown__option--selected');
		}

		// Get all selected values
		const selectedValues = this.getSelectedValues();

		// Update count badge (exclude "all" from count since it's a visual indicator, not a filter)
		const countableValues = selectedValues.filter((v) => v !== 'all');
		this.updateCountBadge(countableValues.length);

		// Navigate to filtered URL
		this.navigateToFilteredUrl(selectedValues);
	}

	getSelectedValues() {
		const values = [];
		this.checkboxes.forEach((cb) => {
			if (cb.checked) {
				values.push(cb.value);
			}
		});
		return values;
	}

	updateCountBadge(count) {
		let badge = this.dropdown.querySelector('.multiselect-dropdown__count');
		const toggleContent = this.dropdown.querySelector('.multiselect-dropdown__toggle-content');

		if (count > 0) {
			if (!badge) {
				badge = document.createElement('span');
				badge.className = 'multiselect-dropdown__count';
				toggleContent.appendChild(badge);
			}
			badge.textContent = count;
		} else if (badge) {
			badge.remove();
		}
	}

	navigateToFilteredUrl(selectedValues) {
		const url = new URL(this.baseUrl, window.location.origin);

		// Add preserved params
		Object.entries(this.preserveParams).forEach(([key, value]) => {
			if (value) {
				url.searchParams.set(key, value);
			}
		});

		// Add selected values
		if (selectedValues.length > 0) {
			url.searchParams.set(this.name, selectedValues.join(','));
		}

		// In AJAX mode, dispatch event instead of navigating
		// Let the consumer (e.g., degree-archive) handle URL state management
		if (this.useAjax) {
			const event = new CustomEvent('multiselect:change', {
				bubbles: true,
				detail: {
					name: this.name,
					values: selectedValues,
					url: url.toString(),
				},
			});
			this.dropdown.dispatchEvent(event);
			return;
		}

		window.location.href = url.toString();
	}

	/**
	 * Get selected values without navigating (for AJAX use cases)
	 */
	getSelected() {
		return this.getSelectedValues();
	}

	/**
	 * Set selected values programmatically
	 */
	setSelected(values) {
		this.checkboxes.forEach((cb) => {
			const option = cb.closest('.multiselect-dropdown__option');
			const shouldBeChecked = values.includes(cb.value);

			cb.checked = shouldBeChecked;

			if (shouldBeChecked) {
				option.classList.add('multiselect-dropdown__option--selected');
			} else {
				option.classList.remove('multiselect-dropdown__option--selected');
			}
		});

		// Exclude "all" from count since it's a visual indicator, not a filter
		const countableValues = values.filter((v) => v !== 'all');
		this.updateCountBadge(countableValues.length);
	}
}

/**
 * Auto-initialize all multiselect dropdowns on the page
 */
function initMultiselectDropdowns() {
	const dropdowns = document.querySelectorAll('[data-multiselect="true"]');
	dropdowns.forEach((el) => {
		// Avoid re-initializing
		if (!el.dataset.multiselectInitialized) {
			new MultiselectDropdown(el);
			el.dataset.multiselectInitialized = 'true';
		}
	});
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initMultiselectDropdowns);
} else {
	initMultiselectDropdowns();
}

// Export for use as a module
if (typeof module !== 'undefined' && module.exports) {
	module.exports = MultiselectDropdown;
}

// Also expose globally for non-module usage
window.MultiselectDropdown = MultiselectDropdown;
window.initMultiselectDropdowns = initMultiselectDropdowns;
